import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const HTProduction = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    die_no: '',
    furnace_code: ''
  });

  const [formData, setFormData] = useState({
    die_no: '',
    heat_symbol: '',
    ht_date: new Date().toISOString().split('T')[0],
    quantity1: '',
    quantity2: '',
    quantity3: '',
    tonnage1: '',
    tonnage2: '',
    tonnage3: '',
    ht_code: '',
    furnace_code: '',
    rework: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { api } = useAuth();

  // Fetch HT Production data
  const { data: htProductionData, isLoading, error, refetch } = useQuery(
    ['htProduction', page, rowsPerPage, searchTerm, filters],
    async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/heat-treatment/production?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching HT Production data: ${error.message}`, { variant: 'error' });
      }
    }
  );

  // Fetch Die Master for dropdown
  const { data: dieMasterData } = useQuery('dieMaster', async () => {
    const response = await api.get('/masters/dies');
    return response.data;
  });

  // Fetch HT Code Master for dropdown
  const { data: htCodeData } = useQuery('htCodes', async () => {
    const response = await api.get('/masters/ht-codes');
    return response.data;
  });

  // Create mutation
  const createMutation = useMutation(
    (data) => api.post('/heat-treatment/production', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('htProduction');
        enqueueSnackbar('HT Production record created successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error creating record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/heat-treatment/production/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('htProduction');
        enqueueSnackbar('HT Production record updated successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => api.delete(`/heat-treatment/production/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('htProduction');
        enqueueSnackbar('HT Production record deleted successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      quantity1: formData.quantity1 ? parseInt(formData.quantity1) : null,
      quantity2: formData.quantity2 ? parseInt(formData.quantity2) : null,
      quantity3: formData.quantity3 ? parseInt(formData.quantity3) : null,
      tonnage1: formData.tonnage1 ? parseFloat(formData.tonnage1) : null,
      tonnage2: formData.tonnage2 ? parseFloat(formData.tonnage2) : null,
      tonnage3: formData.tonnage3 ? parseFloat(formData.tonnage3) : null
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.ht_id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      die_no: item.die_no || '',
      heat_symbol: item.heat_symbol || '',
      ht_date: item.ht_date || '',
      quantity1: item.quantity1 || '',
      quantity2: item.quantity2 || '',
      quantity3: item.quantity3 || '',
      tonnage1: item.tonnage1 || '',
      tonnage2: item.tonnage2 || '',
      tonnage3: item.tonnage3 || '',
      ht_code: item.ht_code || '',
      furnace_code: item.furnace_code || '',
      rework: item.rework || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      die_no: '',
      heat_symbol: '',
      ht_date: new Date().toISOString().split('T')[0],
      quantity1: '',
      quantity2: '',
      quantity3: '',
      tonnage1: '',
      tonnage2: '',
      tonnage3: '',
      ht_code: '',
      furnace_code: '',
      rework: ''
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this HT Production record?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading HT Production data: {error.message}
        </Alert>
      </Box>
    );
  }

  const htProduction = htProductionData?.data?.htProduction || [];
  const totalCount = htProductionData?.data?.pagination?.total || 0;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Heat Treatment Production
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by die no, heat symbol..."
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Die No"
              value={filters.die_no}
              onChange={(e) => setFilters({ ...filters, die_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Furnace Code"
              value={filters.furnace_code}
              onChange={(e) => setFilters({ ...filters, furnace_code: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add HT Production
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>HT Date</TableCell>
              <TableCell>Die No</TableCell>
              <TableCell>Heat Symbol</TableCell>
              <TableCell>HT Code</TableCell>
              <TableCell>Furnace</TableCell>
              <TableCell>Qty1</TableCell>
              <TableCell>Qty2</TableCell>
              <TableCell>Qty3</TableCell>
              <TableCell>Tonnage1</TableCell>
              <TableCell>Tonnage2</TableCell>
              <TableCell>Tonnage3</TableCell>
              <TableCell>Rework</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : htProduction.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No HT Production records found
                </TableCell>
              </TableRow>
            ) : (
              htProduction.map((item) => (
                <TableRow key={item.ht_id}>
                  <TableCell>{formatDate(item.ht_date)}</TableCell>
                  <TableCell>{item.die_no}</TableCell>
                  <TableCell>{item.heat_symbol}</TableCell>
                  <TableCell>{item.ht_code}</TableCell>
                  <TableCell>{item.furnace_code}</TableCell>
                  <TableCell>{item.quantity1}</TableCell>
                  <TableCell>{item.quantity2}</TableCell>
                  <TableCell>{item.quantity3}</TableCell>
                  <TableCell>{item.tonnage1}</TableCell>
                  <TableCell>{item.tonnage2}</TableCell>
                  <TableCell>{item.tonnage3}</TableCell>
                  <TableCell>
                    {item.rework === 'Y' ? <Chip label="Yes" color="warning" size="small" /> : 
                     item.rework === 'N' ? <Chip label="No" color="success" size="small" /> : ''}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.ht_id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingItem ? 'Edit HT Production' : 'Add New HT Production'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Die Number</InputLabel>
                  <Select
                    value={formData.die_no}
                    onChange={(e) => setFormData({ ...formData, die_no: e.target.value })}
                    label="Die Number"
                  >
                    {(dieMasterData?.data?.dies || dieMasterData?.data || []).map((die) => (
                      <MenuItem key={die.die_no} value={die.die_no}>
                        {die.die_no} - {die.part_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Heat Symbol"
                  value={formData.heat_symbol}
                  onChange={(e) => setFormData({ ...formData, heat_symbol: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="HT Date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.ht_date}
                  onChange={(e) => setFormData({ ...formData, ht_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>HT Code</InputLabel>
                  <Select
                    value={formData.ht_code}
                    onChange={(e) => setFormData({ ...formData, ht_code: e.target.value })}
                    label="HT Code"
                  >
                    {htCodeData?.data?.map((code) => (
                      <MenuItem key={code.ht_code} value={code.ht_code}>
                        {code.ht_code} - {code.ht_process}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Furnace Code"
                  value={formData.furnace_code}
                  onChange={(e) => setFormData({ ...formData, furnace_code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Rework</InputLabel>
                  <Select
                    value={formData.rework}
                    onChange={(e) => setFormData({ ...formData, rework: e.target.value })}
                    label="Rework"
                  >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="Y">Yes</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Quantities</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity 1"
                  value={formData.quantity1}
                  onChange={(e) => setFormData({ ...formData, quantity1: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity 2"
                  value={formData.quantity2}
                  onChange={(e) => setFormData({ ...formData, quantity2: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity 3"
                  value={formData.quantity3}
                  onChange={(e) => setFormData({ ...formData, quantity3: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Tonnages</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.001"
                  label="Tonnage 1"
                  value={formData.tonnage1}
                  onChange={(e) => setFormData({ ...formData, tonnage1: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.001"
                  label="Tonnage 2"
                  value={formData.tonnage2}
                  onChange={(e) => setFormData({ ...formData, tonnage2: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.001"
                  label="Tonnage 3"
                  value={formData.tonnage3}
                  onChange={(e) => setFormData({ ...formData, tonnage3: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default HTProduction;